import { Settings, BookOpen, Moon, LogOut, ChevronRight } from 'lucide-react';
import { Switch } from '../ui/switch';
import { cn } from '@/lib/utils';
import { useCopy } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useUIState } from '../../contexts/UIStateContext';
import MobileScreen from './MobileScreen';

interface AjustesScreenProps {
  onSair: () => void;
}

/**
 * Conta e preferências no celular.
 *
 * Substitui a ponte que mandava direto para Configurações fingindo ser outra
 * coisa. O critério aqui é o mesmo do resto do app: nada aparece na tela se não
 * funciona. Por isso a lista é curta e cada linha vai a um lugar que existe.
 *
 * O tema fica aqui, e não escondido dentro de Configurações, porque trocar
 * claro/escuro é a preferência que se mexe no celular (sol na rua, cama à
 * noite) e não merece três toques.
 */
const AjustesScreen = ({ onSair }: AjustesScreenProps) => {
  const t = useCopy().common.ajustesScreen;
  const { user, profile, logout } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const { changeModule } = useUIState();

  const nome = profile?.full_name || user?.email?.split('@')[0] || '';
  const inicial = (nome || '?').charAt(0).toUpperCase();

  const ir = (modulo: string) => { changeModule(modulo); onSair(); };

  /**
   * A casca única de toda linha desta tela.
   *
   * As quatro linhas dividem a mesma moldura (ícone, título, borda, alvo de
   * toque de 48px) e só divergem no que fica à direita e no tom. Enquanto tema
   * e sair carregavam cópias da casca à mão, qualquer ajuste de padding ou de
   * borda precisava ser feito em três lugares, e o terceiro era o que ficava
   * para trás.
   *
   * Os argumentos vêm em objeto e não em posição porque com tom e slot da
   * direita a chamada posicional viraria `linha('x', Icone, texto, fn,
   * undefined, 'perigo')`, onde o `undefined` no meio é o que ninguém lê.
   */
  const linha = ({
    chave,
    Icone,
    titulo,
    aoTocar,
    // A seta é promessa de "leva a outro lugar": quem resolve na própria linha
    // (o Switch do tema) ou encerra a sessão passa o slot explicitamente.
    extra = <ChevronRight size={18} className="shrink-0 text-[var(--color-text-muted)]" />,
    perigo = false,
    className,
  }: {
    chave: string;
    Icone: typeof Settings;
    titulo: string;
    /** Ausente quando o alvo de toque é o controle da direita, não a linha. */
    aoTocar?: () => void;
    extra?: React.ReactNode;
    /** Tom de ação que tira o usuário do produto (sair). */
    perigo?: boolean;
    className?: string;
  }) => {
    // Botão dentro de botão é DOM inválido e faz o clique da casca engolir o do
    // controle: sem `aoTocar`, quem manda é o filho, e a casca vira `div`.
    const Casca = aoTocar ? 'button' : 'div';
    return (
      <Casca
        key={chave}
        data-testid={`ajuste-${chave}`}
        onClick={aoTocar}
        className={cn(
          'flex items-center gap-3 px-4 py-3.5 w-full text-left',
          'bg-[var(--color-bg)] border border-[var(--color-border)] rounded-[var(--radius-md)]',
          perigo && 'text-[var(--color-danger)]',
          className
        )}
      >
        {/* No tom de perigo o ícone e o título herdam a cor da casca: uma linha
            inteira em vermelho lê como um aviso só, e não como três pedaços. */}
        <Icone size={18} className={cn('shrink-0', !perigo && 'text-[var(--color-text-muted)]')} />
        <span className={cn('flex-1 text-[15px]', !perigo && 'text-[var(--color-text)]')}>{titulo}</span>
        {extra}
      </Casca>
    );
  };

  return (
    <MobileScreen titulo={t.titulo}>

      <div className="flex items-center gap-3 px-4 py-4 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-[var(--radius-md)] mb-5">
        <div className="w-12 h-12 rounded-full bg-[var(--color-primary)] text-white grid place-items-center text-[18px] font-semibold shrink-0">
          {inicial}
        </div>
        <div className="min-w-0">
          <div className="text-[15px] font-medium text-[var(--color-text)] truncate">{nome}</div>
          <div className="text-[11px] text-[var(--color-text-secondary)] truncate">{user?.email}</div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {linha({ chave: 'config', Icone: Settings, titulo: t.configuracoes, aoTocar: () => ir('settings') })}
        {linha({ chave: 'ajuda', Icone: BookOpen, titulo: t.ajuda, aoTocar: () => ir('wiki') })}
        {/* Interruptor, e nao linha com "Trocar": a versao anterior mostrava o
            tema ATUAL ao lado da palavra Trocar, e nao dava para saber se
            aquilo era estado ou acao. Switch diz as duas coisas de uma vez, e e
            o primitivo que a tela de notificacoes ja usa para o mesmo papel. */}
        {linha({
          chave: 'tema',
          Icone: Moon,
          titulo: t.temaEscuro,
          // Testid próprio: a casca já carrega `ajuste-tema`, e dois elementos
          // com o mesmo id deixariam o seletor do e2e ambíguo. A linha é o que
          // se afirma na tela; o interruptor é o que se aciona.
          extra: (
            <Switch
              data-testid="ajuste-tema-interruptor"
              checked={resolvedTheme === 'dark'}
              onCheckedChange={(ligado) => setTheme(ligado ? 'dark' : 'light')}
              className="data-[state=checked]:bg-[var(--color-primary)] data-[state=unchecked]:bg-[var(--color-border)]"
            />
          ),
        })}
      </div>

      {/* Sair fica separado e por último: é a única ação desta tela que tira o
          usuário do produto, e não deve encostar nas linhas de navegação. */}
      {linha({
        chave: 'sair',
        Icone: LogOut,
        titulo: t.sair,
        aoTocar: () => logout(),
        extra: null,
        perigo: true,
        className: 'mt-6',
      })}
    </MobileScreen>
  );
};

export default AjustesScreen;
